import React, { useEffect, useRef, useState } from 'react';
import { Template1ResumeData, flattenSkills, filterNullEntries } from './Template1';
import { ResumePaginator, prepareSectionsForPagination, PageMargins } from '@/utils/resumePagination';

// Helper to extract Description string from potentially nested VectorShift objects
// Handles: string, {Description: string}, {Description: {_type, value}}, etc.
const extractDescription = (desc: any): string => {
  if (!desc) return '';
  // Already a string
  if (typeof desc === 'string') return desc;
  // Object with Description field
  if (typeof desc === 'object') {
    const descField = desc.Description;
    if (typeof descField === 'string') return descField;
    // Nested object like {_type: "MaxDepthReached", value: "..."}
    if (typeof descField === 'object' && descField?.value) {
      return String(descField.value);
    }
    // Try direct value field
    if (desc.value) return String(desc.value);
  }
  return '';
};

interface PaginatedTemplate1Props {
  data: Template1ResumeData;
  margins?: PageMargins;
}

export const PaginatedTemplate1: React.FC<PaginatedTemplate1Props> = ({ data, margins }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paginatedContent, setPaginatedContent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create temporary container to build unpaginated content
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.width = '8.5in';
    
    // Build all sections
    const sections: HTMLElement[] = [];

    // Header Section
    const header = document.createElement('header');
    header.className = 'text-center border-b border-gray-400 pb-2 mb-3';
    header.setAttribute('data-keep-together', 'true');
    header.innerHTML = `
      <h1 class="text-2xl font-bold text-gray-900 mb-1">${data.Name}</h1>
      <div class="text-xs text-gray-600 leading-tight">
        ${data.Phone} • ${data.City}, ${data.State} • ${data.Email}
        ${data.LinkedIn ? ` • ${data.LinkedIn}` : ''}
      </div>
    `;
    sections.push(header);

    // Summary Section
    const summary = document.createElement('section');
    summary.className = 'mb-3';
    summary.innerHTML = `
      <p class="text-gray-800 text-xs leading-snug text-justify mb-0" style="padding-left: 0.4in; padding-right: 0.4in;">${data.Summary}</p>
    `;
    sections.push(summary);

    // Skills Section (moved to after summary) - Support both SkillRows and Skills formats
    const allSkills = flattenSkills(data);
    if (allSkills.length > 0) {
      const skills = document.createElement('section');
      skills.className = 'mb-3';
      
      // Group skills into lines of 5
      const skillLines: string[] = [];
      for (let i = 0; i < allSkills.length; i += 5) {
        const lineSkills = allSkills.slice(i, i + 5);
        skillLines.push(lineSkills.join(' • '));
      }
      
      skills.innerHTML = `
        <h3 class="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1">
          Skills
        </h3>
        <div class="text-xs text-gray-800 leading-snug mb-0 text-center">
          ${skillLines.map(line => `<div>${line}</div>`).join('')}
        </div>
      `;
      sections.push(skills);
    }

    // Career Highlights
    if (data.CareerHighlights && data.CareerHighlights.length > 0) {
      const highlights = document.createElement('section');
      highlights.className = 'mb-3';
      const highlightsHtml = data.CareerHighlights.map(h => `
        <div class="text-xs flex gap-2 items-start">
          <span class="text-blue-900 font-bold flex-shrink-0 -mt-0.5">✓</span>
          <p class="text-justify text-gray-800 leading-snug">
            <span class="font-semibold">${h.chtitle}:</span> ${h.chdescription}
          </p>
        </div>
      `).join('');
      highlights.innerHTML = `
        <h3 class="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1">
          Career Highlights
        </h3>
        <div class="space-y-2 px-10">${highlightsHtml}</div>
      `;
      sections.push(highlights);
    }

    // Professional Experience - Header + First Job must stay together
    if (data.ProfessionalExperience && data.ProfessionalExperience.length > 0) {
      // Combine section title + first job into a single atomic section
      const firstJob = data.ProfessionalExperience[0];
      const expBlock = document.createElement('section');
      expBlock.className = 'mb-2';
      expBlock.setAttribute('data-keep-together', 'true');

      const firstBullets = firstJob.Descriptions.map(desc => {
        const text = extractDescription(desc);
        return text ? `<li class="leading-snug">${text}</li>` : '';
      }).filter(Boolean).join('');

      expBlock.innerHTML = `
        <h3 class="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1">
          Professional Experience
        </h3>
        <div class="flex justify-between items-baseline" style="padding-left: 0.4in; padding-right: 0.4in;">
          <div>
            <span class="font-bold text-xs">${firstJob.company}</span>
            ${firstJob.location ? `<span class="text-xs text-gray-700 ml-2">${firstJob.location}</span>` : ''}
          </div>
          <div class="text-xs text-gray-700">
            ${firstJob.start_date} - ${firstJob.end_date}
          </div>
        </div>
        <div class="text-xs italic text-gray-800 mb-1" style="padding-left: 0.4in; padding-right: 0.4in;">${firstJob.job_title}</div>
        <ul class="list-disc list-inside text-xs text-gray-800 space-y-0.5 ml-2 mb-0" style="padding-left: calc(0.4in + 0.25rem); padding-right: 0.4in;">
          ${firstBullets}
        </ul>
      `;
      sections.push(expBlock);

      // Remaining jobs as independent sections (non-atomic, can split)
      data.ProfessionalExperience.slice(1).forEach((job) => {
        const jobSection = document.createElement('section');
        jobSection.className = 'mb-2';
        jobSection.setAttribute('data-experience-continuation', 'true');
        
        const bullets = job.Descriptions.map(desc => {
          const text = extractDescription(desc);
          return text ? `<li class="leading-snug">${text}</li>` : '';
        }).filter(Boolean).join('');
        
        jobSection.innerHTML = `
          <div class="flex justify-between items-baseline" style="padding-left: 0.4in; padding-right: 0.4in;" data-stick-with-next-list="true">
            <div>
              <span class="font-bold text-xs">${job.company}</span>
              ${job.location ? `<span class="text-xs text-gray-700 ml-2">${job.location}</span>` : ''}
            </div>
            <div class="text-xs text-gray-700">
              ${job.start_date} - ${job.end_date}
            </div>
          </div>
          <div class="text-xs italic text-gray-800 mb-1" style="padding-left: 0.4in; padding-right: 0.4in;" data-stick-with-next-list="true">${job.job_title}</div>
          <ul class="list-disc list-inside text-xs text-gray-800 space-y-0.5 ml-2 mb-0" style="padding-left: calc(0.4in + 0.25rem); padding-right: 0.4in;">
            ${bullets}
          </ul>
        `;
        sections.push(jobSection);
      });
    }

    // Certifications - Filter out null entries
    const validCertifications = filterNullEntries(data.CertificationsAndLicenses);
    if (validCertifications.length > 0) {
      const certs = document.createElement('section');
      certs.className = 'mb-3';
      const certsHtml = validCertifications.map(cert => `
        <div class="text-xs text-gray-800 mb-1">
          <span class="font-semibold">${cert.Certification_or_License_Name}</span>
          ${cert.Institution_Organization ? ` – ${cert.Institution_Organization}` : ''}
          ${cert.Year ? ` – ${cert.Year}` : ''}
        </div>
      `).join('');
      certs.innerHTML = `
        <h3 class="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1">
          Certifications
        </h3>
        ${certsHtml}
      `;
      sections.push(certs);
    }

    // Awards - Filter out null entries
    const validAwards = filterNullEntries(data.AwardsAndHonors);
    if (validAwards.length > 0) {
      const awards = document.createElement('section');
      awards.className = 'mb-3';
      const awardsHtml = validAwards.map(award => `
        <div class="text-xs text-gray-800 mb-1">
          <span class="font-semibold">${award.Award_Name}</span>
          ${award.Institution_Organization ? ` – ${award.Institution_Organization}` : ''}
          ${award.Year ? ` – ${award.Year}` : ''}
        </div>
      `).join('');
      awards.innerHTML = `
        <h3 class="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1">
          Achievements
        </h3>
        ${awardsHtml}
      `;
      sections.push(awards);
    }


    // Rules Section
    if (data.Rules) {
      const rules = document.createElement('section');
      rules.className = 'mb-3';
      rules.innerHTML = `
        <h3 class="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1">
          Resume Writing Rules
        </h3>
        <p class="text-xs text-gray-800 leading-snug mb-0 text-justify" style="padding-left: 0.4in; padding-right: 0.4in;">
          ${data.Rules}
        </p>
      `;
      sections.push(rules);
    }

    // Volunteer - Filter out null entries
    const validVolunteer = filterNullEntries(data.VolunteerMemberships);
    if (validVolunteer.length > 0) {
      const volunteer = document.createElement('section');
      const volHtml = validVolunteer.map(vol => `
        <div class="text-xs text-gray-800 mb-1">
          <span class="font-semibold">${vol.PositionName}</span>
          ${vol.InstitutionOrganization ? ` – ${vol.InstitutionOrganization}` : ''}
          ${vol.Year ? ` – ${vol.Year}` : ''}
        </div>
      `).join('');
      volunteer.innerHTML = `
        <h3 class="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1">
          Volunteer Leadership
        </h3>
        ${volHtml}
      `;
      sections.push(volunteer);
    }

    // Education Section (moved to last) - Handle both Year_Earned and YearEarned
    const education = document.createElement('section');
    education.className = 'mb-3';
    const eduHtml = data.Education.map(edu => `
      <div class="mb-1 text-xs">
        <div class="font-semibold text-gray-900">${edu.College_Name}</div>
        <div class="text-gray-800">
          ${edu.College_Degree_Name}${edu.Major ? `, ${edu.Major}` : ''}
          ${(edu.Year_Earned || edu.YearEarned) ? ` – ${edu.Year_Earned || edu.YearEarned}` : ''}
        </div>
      </div>
    `).join('');
    education.innerHTML = `
      <h3 class="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1">
        Education
      </h3>
      ${eduHtml}
    `;
    sections.push(education);

    // Run pagination with provided or default margins (v1.1 - fixed box-sizing)
    const paginator = new ResumePaginator({
      margins: margins || { top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }
    });

    const paginated = paginator.paginate(tempContainer, sections);
    setPaginatedContent(paginated);

  }, [data, margins]);

  return (
    <div ref={containerRef}>
      {paginatedContent && (
        <div 
          className="font-document"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
          dangerouslySetInnerHTML={{ __html: paginatedContent.innerHTML }} 
        />
      )}
    </div>
  );
};
